import { Suspense, useState, useRef, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Card, Text, ProgressBar, Banner, Page, Box, Button, Tabs } from "@shopify/polaris";
import { Loader, Stack } from "..";
// import AskReview from "../modals/AskReview";
import { TitleBar } from ".";
import {
  APP_ROUTES,
  DATABASE_STATUTES,
  DEFAULT_PRODUCT_FILTER_VALUES,
  SETTINGS_TYPE,
  TAG_AUTOMATION_TUTORIALS_URL,
} from "../../common/constants";
import useLocales from "../../hooks/useLocales";
import useProductsContext from "../../hooks/useProductsContext";
import useShopContext from "../../hooks/useShopContext";
import { capitalEvery } from "../../utils/textEnhancers";
import moment from "moment";

const PRODUCTS_SIDE_NAV_CONFIG = [
  { to: APP_ROUTES.PRODUCTS.PREORDERS, icon: "mdi:cart-check", title: "nav.pre-orders" },
  { to: APP_ROUTES.PRODUCTS.INSTOCK, icon: "mdi:store-check-outline", title: "nav.in-stock" },
  { to: APP_ROUTES.PRODUCTS.BACKINSTOCK, icon: "icon-park-outline:timer", title: "nav.back-in-stock" },
  { to: APP_ROUTES.PRODUCTS.COMINGSOON, icon: "mdi:store-clock-outline", title: "nav.coming-soon" },
];

const BUTTON_KEY_ALERT = "hideAlertProductPageUntil";

const ProductsLayout = () => {
  const [showAlert, setShowAlert] = useState(false);

  const hideAlert = useCallback(() => {
    const currentDate = moment();
    const nextMonthDate = currentDate.add(1, "months");

    setShowAlert(false);

    localStorage.setItem(BUTTON_KEY_ALERT, nextMonthDate.format());
  }, []);

  const { translate } = useLocales();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    shop: { isDisabled, isDisabledIS, hasMultipleProducts, hasScript, hasCustomScript, needsScriptUpdate },
  } = useShopContext();
  const { statuses, progress, filters, saveFilters } = useProductsContext();
  const productTab = useRef("");

  useEffect(() => {
    // clear filters if a user changed product tab
    if (filters.isActive && pathname.startsWith("/products/")) {
      const tab = pathname.replace(/\/products\//, "");

      if (tabs.includes(tab) && tabs.includes(productTab.current) && productTab.current !== tab) {
        saveFilters({
          values: DEFAULT_PRODUCT_FILTER_VALUES,
          isActive: false,
        });
      }

      productTab.current = tab;
    }

    // clear filters if a user left product tabs
    return () => {
      if (filters.isActive) {
        saveFilters({
          values: DEFAULT_PRODUCT_FILTER_VALUES,
          isActive: false,
        });
      }
    };
  }, [filters.isActive, pathname, saveFilters]);

  useEffect(() => {
    const checkVisibility = (key, setState) => {
      const hideUntil = localStorage.getItem(key);
      const currentDate = moment();
      const nextVisibleDate = moment(hideUntil);

      if (hideUntil && nextVisibleDate.isAfter(currentDate)) {
        setState(false);
      } else {
        setState(true);
      }
    };

    checkVisibility(BUTTON_KEY_ALERT, setShowAlert);
  }, []);

  const REDIRECT_TABS = {
    [APP_ROUTES.PRODUCTS.BACKINSTOCK]: SETTINGS_TYPE.BIS,
    [APP_ROUTES.PRODUCTS.COMINGSOON]: SETTINGS_TYPE.CS,
    [APP_ROUTES.PRODUCTS.INSTOCK]: SETTINGS_TYPE.IS,
    [APP_ROUTES.PRODUCTS.PREORDERS]: SETTINGS_TYPE.PO,
  };

  const isAddProductDisabled =
    (isDisabled && pathname === APP_ROUTES.PRODUCTS.PREORDERS) ||
    (isDisabledIS && pathname === APP_ROUTES.PRODUCTS.INSTOCK);

  const handleNavigateToAddProducts = () => {
    navigate(APP_ROUTES.PRODUCTS.ADD.ROOT, { state: { redirectTab: REDIRECT_TABS[pathname] } });
  };

  const [selectedTab, setSelectedTab] = useState(0);
  const tabs = PRODUCTS_SIDE_NAV_CONFIG.map((item) => ({
    id: item.title,
    content: capitalEvery(translate(item.title)),
    accessibilityLabel: item.title,
    panelID: item.key,
    to: item.to,
  }));

  const handleTabChange = (index) => {
    setSelectedTab(index);
    navigate(PRODUCTS_SIDE_NAV_CONFIG[index].to);
  };

  useEffect(() => {
    if (tabs.length) {
      const tab = tabs.findIndex((item) => item.to === pathname);
      if (selectedTab !== tab && tab > -1) {
        setSelectedTab(tab);
      }
    }
  }, [pathname, tabs]);

  return (
    <Page fullWidth>
      <Box paddingInline={{ xs: "400", sm: "0" }}>
        <TitleBar
          heading={translate("nav.sub.products")}
          action={
            <>
              <Button disabled={isAddProductDisabled} variant="primary" onClick={handleNavigateToAddProducts}>
                {translate("page.products.add-button")}
              </Button>
            </>
          }
        />

        <Stack spacing={2} width="100%" flexDirection="column">
          <Stack spacing={2} flexDirection="column">
            {statuses.savePreorder === DATABASE_STATUTES.IN_PROGRESS && (
              <Card>
                <Text variant="headingMd">{translate("page.products.db-in-progress")}</Text>
                <ProgressBar progress={progress} size="small" />
              </Card>
            )}

            {hasCustomScript && needsScriptUpdate && (
              <Banner title={translate("page.product.alert-update-title")} tone="critical">
                {translate("page.product.alert-update-message")}
              </Banner>
            )}

            {hasScript && !hasCustomScript && needsScriptUpdate && (
              <Banner title={translate("page.product.alert-update-script-title")} tone="critical">
                {translate("page.product.alert-update-script-message")}
              </Banner>
            )}

            {hasMultipleProducts && (
              <Banner title={translate("page.product.alert-multiple-products-title")} tone="warning">
                {translate("page.product.alert-multiple-products-message")}
              </Banner>
            )}

            {showAlert && (
              <Banner
                title={translate("page.products.banner-title")}
                action={{ content: translate("page.settings.learn-more"), url: APP_ROUTES.AUTOMATIONS.TAGS }}
                secondaryAction={{ content: translate("common.tutorial-btn"), url: TAG_AUTOMATION_TUTORIALS_URL }}
                tone="info"
                onDismiss={hideAlert}
              >
                {translate("page.products.banner-text-learn")}
              </Banner>
            )}
          </Stack>

          <Box display="flex" width="100%">
            <Card>
              <Box paddingBlock="400">
                <Box className="nav-tabs-container">
                  <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
                </Box>
              </Box>

              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </Card>
          </Box>
        </Stack>

        {/* <AskReview /> */}
      </Box>
    </Page>
  );
};

export default ProductsLayout;
